// Customizable Area Start
import React, { useState } from "react";
import {
  styled,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from "@material-ui/core";
import TopSellingItemController, {
  PreviousYear,
  CurrentYear,
  Props,
  configJSON,
} from "./TopSellingItemController.web";
import { colors } from "../../utilities/src/Colors";
import { IFilter } from "../../../components/src/FilterPopover";
import TopSellingItemChart from "../../../components/src/TopSellingItemChart.web";
import FavoriteItemsTable from "../../../components/src/CustomizeTopSellingitemTable.web";

import DashboardHeader from "../../../components/src/DashboardHeader";
import DashboardTabPanel from "../../../components/src/DashboardTabPanel.web";
import * as assets from "./assets";
import { getGroupNameDisplay } from "../../cfsalesdashboard3/src/utils";
import ExportModal from "../../../components/src//ExportModal";
import { storeNameBasedOnGroup } from "./utils";

export default class TopSellingItems extends TopSellingItemController {
  constructor(props: Props) {
    super(props);
  }

  renderTable = (
    header: string,
    items: Array<CurrentYear | PreviousYear>,
    searchQuery: string,
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    sortConfig: {
      key: keyof CurrentYear | keyof PreviousYear;
      direction: "asc" | "desc";
    } | null,
    table: "current" | "previous",
    headerBgColor?: string,
    fromDate?: string,
    toDate?: string
  ) => {
    const filteredItems = items.filter((item) => {
      const query = searchQuery.toLowerCase();
      return (
        item.catalogue_name.toLowerCase().includes(query) ||
        item.orders.toString().includes(query) ||
        item.pieces.toString().includes(query) ||
        item.amount.toString().includes(query)
      );
    });

    const sortedItems = [...filteredItems].sort((a, b) => {
      if (!sortConfig) return 0;
      const aVal = (a as any)[sortConfig.key];
      const bVal = (b as any)[sortConfig.key];

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      return sortConfig.direction === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 8,
            gap: 35,
          }}
        >
          <input
            type='text'
            placeholder='Search...'
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ padding: 4, width: 200 }}
          />
          {fromDate && toDate && (
            <div style={{ fontWeight: 500, color: "#333" }}>
              {`From: ${fromDate}  |  To: ${toDate}`}
            </div>
          )}
        </div>

        <StyledTable className={header === "Hours" ? "pink-table" : ""}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["catalogue_name", "orders", "pieces", "amount"].map(
                  (key, idx) => (
                    <TableCell
                      key={idx}
                      width='27%'
                      style={{
                        cursor: "pointer",
                        backgroundColor: headerBgColor || "#f0f0f0",
                      }}
                      onClick={() =>
                        this.handleSort(
                          key as keyof CurrentYear | keyof PreviousYear,
                          table
                        )
                      }
                    >
                      <p>
                        {key === "catalogue_name"
                          ? "Product Name"
                          : key.charAt(0).toUpperCase() + key.slice(1)}{" "}
                        {sortConfig?.key === key
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : "⇅"}
                      </p>
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.catalogue_name}</TableCell>
                  <TableCell>{item.orders}</TableCell>
                  <TableCell>{item.pieces}</TableCell>
                  <TableCell>{item.amount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTable>
      </div>
    );
  };

  handleSort = (
    key: keyof CurrentYear | keyof PreviousYear,
    table: "current" | "previous"
  ) => {
    const sortKey =
      table === "current" ? "currentYearSort" : "previousYearSort";

    this.setState((prevState) => {
      const currentSort = prevState[sortKey];

      const newSort =
        currentSort?.key === key
          ? { key, direction: currentSort.direction === "asc" ? "desc" : "asc" }
          : { key, direction: "asc" };

      return {
        [sortKey]: newSort,
      } as any;
    });
  };

  render() {
    const {
      groupId,
      regionName,
      storeId,
      currentYear,
      previousYear,
      activeType,
      loadingDayData,
      loadingHourData,
      favoriteId,
    } = this.state;
   
    const normalize = (str: string) =>
      str?.toLowerCase().trim().replace(/\s+/g, " ");

    const sortedCurrent = [...currentYear]
      .sort((a, b) => (b.amount || 0) - (a.amount || 0))
      .slice(0, 5);

    const chartData = sortedCurrent.map((current) => {
      const previous = previousYear.find(
        (p) => normalize(p.catalogue_name) === normalize(current.catalogue_name)
      );

      return {
        catalogue_id: Number(current.catalogue_id) || 0,
        name: current.catalogue_name.trim(),

        current_orders: current.orders || 0,
        current_pieces: current.pieces || 0,
        current_amount: current.amount || 0,

        previous_orders: previous?.orders || 0,
        previous_pieces: previous?.pieces || 0,
        previous_amount: previous?.amount || 0,
      };
    });
    const finalChartData =
      favoriteId.length > 0
        ? favoriteId
            .map((fav) => {
              const current = currentYear.find(
                (item) => Number(item.catalogue_id) === Number(fav.id)
              );
              const previous = previousYear.find(
                (item) =>
                  normalize(item.catalogue_name) ===
                  normalize(current?.catalogue_name || "")
              );
              return {
                catalogue_id: Number(fav.id),
                name: current?.catalogue_name?.trim() || fav.name || "-",

                current_orders: current?.orders || 0,
                current_pieces: current?.pieces || 0,
                current_amount: current?.amount || 0,
                previous_orders: previous?.orders || 0,
                previous_pieces: previous?.pieces || 0,
                previous_amount: previous?.amount || 0,
              };
            })
            .filter((item) => item.current_amount > 0)
            .sort((a, b) => b.current_amount - a.current_amount)
            .slice(0, 5)
        : chartData;

    return (
      <>
        <div>
          <DashboardTabPanel
            navigation={this.props.navigation}
            id='TopSellingItems'
            permissionStatus={this.state.permissionStatus}
          />
          <StyledWrapper
            id={this.state.isPrinting ? "print-wrapper" : undefined}
            //   className={this.getPrintClass()}
          >
            <DashboardHeader
              filterLoading={false}
              isAppliedFilter={this.state.isAppliedFilter}
              regionVal={regionName}
              storeVal={storeNameBasedOnGroup(storeId)}
              isGroup={true}
              groupVal={getGroupNameDisplay(groupId)}
              handleFilterButton={this.handleFilterButton}
              handleCloseFilterButton={this.handleCloseFilterButton}
              handleFilterChange={this.handleFilterChange}
              filterAnchor={this.state.filterAnchor}
              filters={this.state.filters as IFilter[]}
              periodDateRange={this.periodDateRange()}
              timeRange={this.timeRange()}
              elementBeforeFilter={<div />}
              elementAfterFilter={
                <div className='actions'>
                  <button
                    type='button'
                    onClick={() => this.handleOpenExportModal()}
                    disabled={!currentYear.length && !previousYear.length}
                  >
                    {configJSON.exportAll}
                  </button>
                </div>
              }
            />
            <p className='title'>Top Selling Items</p>

            <div className='main-content'>
              <div className='main-content-section'>
               <FavoriteItemsTable
                  currentYear={this.state.parentCurrentYear.map((item) => ({
                    ...item,
                    catalogue_id: Number(item.catalogue_id) || 0,
                  }))}
                  favoriteId={favoriteId}
                  handleStatusChangeForSelected={
                    this.handleStatusChangeForSelected
                  }
                />
              </div>

              <div className='main-content-section'>
                <TopSellingItemChart
                  chartData={finalChartData}
                  barCategoryGap='30%'
                  isPrinting={this.state.isPrinting}
                  loading={loadingDayData}
                />
              </div>
            </div>

            <div className='main-content'>
              <div className='main-content-section'>
                <div className='section-table'>
                  {this.renderTable(
                    "Days",
                    currentYear.filter((item) => item.orders || item.pieces),
                    this.state.searchQuery, 
                    (e) => this.setState({ searchQuery: e.target.value }),
                    this.state.currentYearSort,
                    "current",
                    "#d0f0c0",
                    this.state.currentYearRange.fromDate,
                    this.state.currentYearRange.toDate
                  )}
                </div>
              </div>

              <div className='main-content-section'>
                <div className='section-table'>
                  {this.renderTable(
                    "Days",
                    previousYear.filter((item) => item.orders || item.pieces),
                    this.state.searchQuery, 
                    (e) => this.setState({ searchQuery: e.target.value }),
                    this.state.previousYearSort,
                    "previous",
                    "#f0d0d0",
                    this.state.previousYearRange.fromDate,
                    this.state.previousYearRange.toDate
                  )}
                </div>
              </div>
            </div>
          </StyledWrapper>
        </div>
        {this.state.openExportModal && (
          <ExportModal
            isOpen
            onCancel={this.handleOpenExportModal}
            onConfirm={this.handleExportConfirm}
          />
        )}
      </>
    );
  }
}

const StyledWrapper = styled("div")({
  "& .custom-loading": {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgb(0, 0, 0, .4)",
    zIndex: 1400,
  },

  "&#print-wrapper": {
    transform: "scale(1)",
    "& .d-flex": {
      "& .filterButtonAccount, & .actions": {
        display: "none",
      },
      "& .switch": {
        marginRight: 0,
        border: 0,
        "& button.active-toggle": {
          background: "white",
          border: 0,
        },
        "& button.inactive-toggle": {
          display: "none",
        },
      },
    },
    "& .title, & .main-content": {
      marginTop: 10,
      marginBottom: 0,
    },
    "& .main-content": {
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "unset",
    },
    "& .section-table": {
      paddingTop: 4,
      "& > .MuiTableContainer-root": {
        maxHeight: "unset",
      },
    },
    "&.long-layout": {
      "& .section-table .MuiTableCell-root": {
        paddingTop: 6,
        paddingBottom: 10,
      },
    },
    "&.extra-long-layout": {
      "& .title, & .main-content": {
        marginTop: 5,
      },
      "& .section-table .MuiTableCell-root": {
        padding: 4,
      },
    },
  },
  "& .circular-container": {
    position: "absolute",
    left: "50%",
    top: "40%",
    transform: "translate(-50%, -50%)",
  },
  "& .headerWrapper": {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 12,
    marginTop: 10,
    alignItems: "center",
  },
  "& .rangeBox": {
    display: "flex",
    gap: 40,
  },
  "& .PageHeading": {
    fontFamily: "Montserrat",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "24px",
    color: colors().darkjunglegreen,
    "@media only screen and (max-width: 1024px)": {
      fontSize: "16px",
    },
  },
  "& .storeval": {
    marginLeft: "50px",
  },
  "& .datePeriod": {
    fontFamily: "Montserrat",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
  },
  "& .d-flex": {
    display: "flex",
  },
  "& .flexstyle": {
    flexWrap: "wrap",
    gap: 20,
  },
  "& .filterButtonAccount": {
    "&.active-border": {
      borderColor: colors().cyancobaltblue,
    },
    cursor: "pointer",
    background: colors().white,
    height: "44px",
    width: "44px",
    fontSize: "24px",
    position: "relative",
    borderColor: colors().lightborder,
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
    color: colors().darkliver,
    border: "1px solid",
    alignItems: "center",
    "@media only screen and (max-width: 1024px)": {
      height: 44,
      width: 44,
    },
    "& .filterActiveIcon": {
      minWidth: "15px",
      top: "-5px",
      right: "-5px",
      position: "absolute",
      background: "#204B9C",
      minHeight: "15px",
      borderRadius: "50%",
    },
  },
  "& .switch": {
    marginRight: 16,
    display: "grid",
    width: "fit-content",
    gridTemplateColumns: "auto auto",
    border: `solid 1px ${colors().lightborder}`,
    borderRadius: 8,
    "& > button": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "10px 16px",
      fontSize: 16,
      lineHeight: 1.5,
      border: 0,
      background: "white",
      "&:first-of-type": {
        borderRadius: "8px 0 0 8px",
      },
      "&:last-of-type": {
        borderRadius: "0 8px 8px 0",
      },
      "&.active-toggle": {
        background: colors().brightgray,
        pointerEvents: "none",
      },
    },
  },
  "& .actions": {
    marginLeft: 18,
    display: "flex",
    gap: 16,
    "& > button": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "10px 16px",
      lineHeight: 1.5,
      border: 0,
      borderRadius: 8,
      background: colors().lightgray,
      fontWeight: 600,
      cursor: "pointer",
      height: 44,
      fontSize: 16,
    },
  },
  "& .title": {
    fontSize: 19,
    fontWeight: 600,
    lineHeight: 1.2,
    marginTop: 20,
  },
  "& .main-content": {
    margin: "22px 0",
    paddingBottom: 20,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    "@media only screen and (max-width: 1200px)": {
      gridTemplateColumns: "unset",
      gridTemplateRows: "1fr 1fr",
    },
  },
  "& .main-content-section": {
    borderRadius: 12,
    border: `solid 1px ${colors().lightborder}`,
  },
  "& .section-title": {
    fontSize: 19,
    fontWeight: 500,
    lineHeight: 1.2,
    padding: 16,
  },
  "& .section-table": {
    padding: 16,
  },
  "& .custom-bar-chart-container": {
    marginLeft: -8,
  },
});

const StyledTable = styled(TableContainer)({
  maxHeight: 500,
  borderRadius: 16,
  background: colors().white,
  boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
  overflowY: "auto", 

  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: "0 4px", 
    minWidth: "100%",
  },

  "& .MuiTableCell-root": {
    borderBottom: "none",
    "&.MuiTableCell-head": {
      fontSize: 14,
      fontWeight: 700,
      color: colors().darkjunglegreen,
      backgroundColor: "#f0f0f0",
      padding: "8px 12px", 
      textTransform: "uppercase",
      letterSpacing: 0.5,
      position: "sticky",
      top: 0,
      zIndex: 100,
    },
    "&.MuiTableCell-body": {
      fontSize: 14,
      fontWeight: 500,
      color: colors().darkliver,
      padding: "6px 12px", 
      backgroundColor: "transparent",
    },
  },

  "& .MuiTableBody-root .MuiTableRow-root": {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#cce6ff", 
    },

    "&:nth-of-type(odd)": {
      backgroundColor: "#e6f2ff", 
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#ffffff",
    },
  },
});
